// File: frontend/src/pages/Socrates/components/AIAgents/index.js

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faRobot, faPlus, faSearch, 
  faFilter, faGrip, faList 
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useAIAgents } from '../../contexts/AIAgentsContext';
import SocratesCard from '../common/SocratesCard';
import LoadingSpinner from '../common/LoadingSpinner';
import styles from './AIAgents.module.css';

const AIAgents = ({ agentName }) => {
  const { t } = useLanguage();
  const { 
    agents, 
    selectedAgent,
    loading, 
    error, 
    loadAgents, 
    selectAgent,
    clearSelectedAgent 
  } = useAIAgents();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [filterStatus, setFilterStatus] = useState('all'); // 'all' | 'active' | 'inactive'

  // Load agents on mount
  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  // Select agent based on URL parameter
  useEffect(() => {
    if (agentName && agents.length > 0) {
      selectAgent(agentName);
    } else if (!agentName) {
      clearSelectedAgent();
    }
  }, [agentName, agents, selectAgent, clearSelectedAgent]);

  // Filter agents based on search and status
  const filteredAgents = agents.filter(agent => {
    const matchesSearch = agent.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         agent.plato_agent_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (agent.description && agent.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = filterStatus === 'all' || agent.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  if (loading && agents.length === 0) {
    return <LoadingSpinner size="large" message={t('loading_ai_agents')} />;
  }

  return (
    <div className={styles.aiAgents}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>
            <FontAwesomeIcon icon={faRobot} className={styles.titleIcon} />
            {t('ai_agents')}
          </h1>
          <p className={styles.subtitle}>{t('ai_agents_description')}</p>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.createButton}>
            <FontAwesomeIcon icon={faPlus} />
            <span>{t('create_agent')}</span>
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className={styles.controls}>
        <div className={styles.searchContainer}>
          <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
          <input
            type="text"
            placeholder={t('search_agents')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        
        <div className={styles.filters}>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">{t('all_statuses')}</option>
            <option value="active">{t('active')}</option>
            <option value="inactive">{t('inactive')}</option>
          </select>
        </div>

        <div className={styles.viewToggle}>
          <button
            className={`${styles.viewButton} ${viewMode === 'grid' ? styles.active : ''}`}
            onClick={() => setViewMode('grid')}
          >
            <FontAwesomeIcon icon={faGrip} />
          </button>
          <button
            className={`${styles.viewButton} ${viewMode === 'list' ? styles.active : ''}`}
            onClick={() => setViewMode('list')}
          >
            <FontAwesomeIcon icon={faList} />
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className={styles.errorBanner}>
          <span>{error}</span>
        </div>
      )}

      {/* Agents Grid/List */}
      {filteredAgents.length === 0 ? (
        <div className={styles.emptyState}>
          <FontAwesomeIcon icon={faRobot} className={styles.emptyIcon} />
          <h3>{t('no_agents_found')}</h3>
          <p>{t('no_agents_description')}</p>
          <button className={styles.createFirstButton}>
            <FontAwesomeIcon icon={faPlus} />
            {t('create_first_agent')}
          </button>
        </div>
      ) : (
        <div className={`${styles.agentsContainer} ${styles[viewMode]}`}>
          {filteredAgents.map((agent) => (
            <SocratesCard 
              key={agent.plato_agent_name} 
              className={`${styles.agentCard} ${selectedAgent?.plato_agent_name === agent.plato_agent_name ? styles.selected : ''}`}
            >
              <div className={styles.agentHeader}>
                <div className={styles.agentIcon}>
                  <FontAwesomeIcon icon={faRobot} />
                </div>
                <div className={styles.agentInfo}>
                  <h3 className={styles.agentName}>{agent.display_name}</h3>
                  <p className={styles.agentId}>{agent.plato_agent_name}</p>
                </div>
                <div className={`${styles.statusBadge} ${styles[agent.status]}`}>
                  {t(agent.status)}
                </div>
              </div>
              
              <p className={styles.agentDescription}>
                {agent.description || t('no_description')}
              </p>
              
              <div className={styles.agentStats}>
                <div className={styles.stat}>
                  <span className={styles.statValue}>{agent.artifacts_count}</span>
                  <span className={styles.statLabel}>{t('artifacts')}</span>
                </div>
                <div className={styles.stat}>
                  <span className={styles.statValue}>{agent.variables_count}</span>
                  <span className={styles.statLabel}>{t('variables')}</span>
                </div>
              </div>
              
              <div className={styles.agentActions}>
                <button className={styles.viewButton}>
                  {t('view_details')}
                </button>
                <button className={styles.editButton}>
                  {t('edit')}
                </button>
              </div>
            </SocratesCard>
          ))}
        </div>
      )}
    </div>
  );
};

export default AIAgents;